import { useState, useEffect } from 'react';
import { getRevenues, createRevenue, updateRevenue, deleteRevenue, getRevenueSummary } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Loader2, Trash2, Pencil, TrendingUp, Calendar, Tag, DollarSign, FileText } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  { value: '01', label: 'Janvier' }, { value: '02', label: 'Fevrier' }, { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' }, { value: '05', label: 'Mai' }, { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' }, { value: '08', label: 'Aout' }, { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' }, { value: '11', label: 'Novembre' }, { value: '12', label: 'Decembre' },
];

const REVENUE_CATEGORIES = [
  { id: 'vente_directe', label: 'Vente directe', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'ecommerce', label: 'E-commerce', color: 'bg-blue-100 text-blue-700' },
  { id: 'services', label: 'Services / Prestations', color: 'bg-violet-100 text-violet-700' },
  { id: 'consulting', label: 'Consulting', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'location', label: 'Location / Loyer', color: 'bg-amber-100 text-amber-700' },
  { id: 'commission', label: 'Commissions', color: 'bg-pink-100 text-pink-700' },
  { id: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-600' },
];

const PAYMENT_METHODS = [
  { id: 'twint', label: 'Twint' },
  { id: 'cash', label: 'Cash' },
  { id: 'virement', label: 'Virement' },
  { id: 'carte', label: 'Carte' },
  { id: 'paypal', label: 'PayPal' },
  { id: 'autre', label: 'Autre' },
];

const RevenuesPage = () => {
  const [revenues, setRevenues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => String(currentYear - i));

  const defaultForm = {
    month: `${filterYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    category: 'vente_directe',
    payment_method: 'cash',
    amount: '',
    description: '',
    currency: 'CHF'
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { loadData(); }, [filterYear]);

  const loadData = async () => {
    try {
      const [revRes, sumRes] = await Promise.all([getRevenues(filterYear), getRevenueSummary(filterYear)]);
      setRevenues(revRes.data);
      setSummary(sumRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Montant requis'); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateRevenue(editing.id, { ...form, amount: parseFloat(form.amount) });
        toast.success('Mis a jour');
      } else {
        await createRevenue({ ...form, amount: parseFloat(form.amount) });
        toast.success('Recette ajoutee');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...defaultForm, month: form.month });
      loadData();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({ month: r.month, category: r.category, payment_method: r.payment_method || 'cash', amount: r.amount.toString(), description: r.description || '', currency: r.currency || 'CHF' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette recette ?')) return;
    try { await deleteRevenue(id); toast.success('Supprime'); loadData(); } catch (e) { toast.error('Erreur'); }
  };

  const handleExportPdf = () => {
    if (!summary) return;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Recettes ${filterYear}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1e293b;padding:30px;line-height:1.5}
h1{font-size:26px;color:#0f172a;margin-bottom:2px;font-weight:800}
h2{font-size:15px;color:#1e40af;margin:24px 0 8px;font-weight:700;border-bottom:2px solid #dbeafe;padding-bottom:4px}
.subtitle{color:#64748b;font-size:13px;margin-bottom:20px}
.kpis{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin-bottom:24px}
.kpi{border:1px solid #e2e8f0;border-radius:10px;padding:14px}
.kpi .label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.5px}
.kpi .val{font-size:22px;font-weight:800;color:#059669;margin-top:4px}
.cat-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid #f1f5f9}
.cat-badge{font-size:10px;padding:2px 10px;border-radius:20px;font-weight:600;background:#ecfdf5;color:#059669;min-width:100px;text-align:center}
.cat-bar{flex:1;height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden}
.cat-bar-fill{height:100%;background:#059669;border-radius:4px}
.cat-amount{font-size:12px;font-weight:600;min-width:100px;text-align:right}
.cat-pct{font-size:11px;color:#94a3b8;min-width:40px;text-align:right}
table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:16px}
th{background:linear-gradient(135deg,#1e40af,#3b82f6);color:white;padding:7px 8px;text-align:right;font-weight:600}
th:first-child{text-align:left;border-radius:6px 0 0 0}
th:last-child{border-radius:0 6px 0 0}
td{padding:5px 8px;border-bottom:1px solid #e2e8f0;text-align:right}
td:first-child{text-align:left;font-weight:500}
tr:nth-child(even){background:#f8fafc}
.total td{font-weight:700;border-top:2px solid #1e40af;background:#eff6ff}
.month-header{background:#f8fafc;font-weight:700}
.month-header td{color:#1e40af;border-bottom:2px solid #dbeafe}
.pm{font-size:9px;color:#64748b;margin-left:4px}
@media print{body{padding:15px}}
</style></head><body>
<h1>Recettes ${filterYear}</h1>
<p class="subtitle">Extrait des recettes enregistrees</p>

<div class="kpis">
<div class="kpi"><p class="label">Total ${filterYear}</p><p class="val">${fmt(summary.total)}</p></div>
<div class="kpi"><p class="label">Entrees</p><p class="val" style="color:#1e293b">${summary.count}</p></div>
<div class="kpi"><p class="label">Moyenne / mois</p><p class="val" style="color:#1e293b">${summary.by_month.length > 0 ? fmt(summary.total / summary.by_month.length) : '-'}</p></div>
<div class="kpi"><p class="label">Categories</p><p class="val" style="color:#1e293b">${summary.by_category.length}</p></div>
</div>

<h2>Repartition par type de recette</h2>
${summary.by_category.map(c => {
  const cat = getCat(c.category);
  const pct = summary.total > 0 ? (c.total / summary.total * 100).toFixed(0) : 0;
  return `<div class="cat-row">
    <span class="cat-badge">${cat.label}</span>
    <div class="cat-bar"><div class="cat-bar-fill" style="width:${pct}%"></div></div>
    <span class="cat-amount">${fmt(c.total)}</span>
    <span class="cat-pct">${pct}%</span>
  </div>`;
}).join('')}

<h2>Detail par mois</h2>
<table>
<tr><th>Mois</th><th>Type</th><th>Paiement</th><th>Description</th><th>Montant</th></tr>
${monthKeys.map(month => {
  const entries = grouped[month];
  const monthTotal = entries.reduce((s, r) => s + r.amount, 0);
  return `<tr class="month-header"><td colspan="4">${getMonthLabel(month)}</td><td>${fmt(monthTotal)}</td></tr>
${entries.map(r => {
  const cat = getCat(r.category);
  return `<tr><td></td><td>${cat.label}</td><td>${r.payment_method ? getPM(r.payment_method) : '-'}</td><td>${r.description || '-'}</td><td>${fmt(r.amount, r.currency)}</td></tr>`;
}).join('')}`;
}).join('')}
<tr class="total"><td colspan="4">Total ${filterYear}</td><td>${fmt(summary.total)}</td></tr>
</table>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const fmt = (n, cur = 'CHF') => new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 2 }).format(n || 0) + ' ' + cur;
  const getMonthLabel = (m) => { const parts = m.split('-'); return MONTHS[parseInt(parts[1]) - 1]?.label + ' ' + parts[0]; };
  const getCat = (id) => REVENUE_CATEGORIES.find(c => c.id === id) || REVENUE_CATEGORIES[REVENUE_CATEGORIES.length - 1];
  const getPM = (id) => PAYMENT_METHODS.find(p => p.id === id)?.label || id;

  // Group by month
  const grouped = {};
  revenues.forEach(r => {
    if (!grouped[r.month]) grouped[r.month] = [];
    grouped[r.month].push(r);
  });
  const monthKeys = Object.keys(grouped).sort().reverse();

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="revenues-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Recettes</h1>
          <p className="text-slate-500 mt-1">Saisie rapide de vos revenus par mois — ideal pour la declaration fiscale</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[120px]" data-testid="revenue-year-filter">
              <Calendar className="w-4 h-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportPdf} disabled={!summary || revenues.length === 0} data-testid="export-revenues-pdf">
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button onClick={() => { setEditing(null); setForm({ ...defaultForm, month: `${filterYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}` }); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-revenue-btn">
            <Plus className="w-4 h-4 mr-2" />Ajouter
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-emerald-500" /><span className="text-xs text-slate-500">Total {filterYear}</span></div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="revenue-total">{fmt(summary.total)}</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Calendar className="w-4 h-4 text-blue-500" /><span className="text-xs text-slate-500">Entrees</span></div>
            <p className="text-2xl font-bold">{summary.count}</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-indigo-500" /><span className="text-xs text-slate-500">Moyenne / mois</span></div>
            <p className="text-2xl font-bold">{summary.by_month.length > 0 ? fmt(summary.total / summary.by_month.length) : '–'}</p>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Tag className="w-4 h-4 text-purple-500" /><span className="text-xs text-slate-500">Categories</span></div>
            <p className="text-2xl font-bold">{summary.by_category.length}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Category breakdown */}
      {summary && summary.by_category.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-base">Repartition par type de recette</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.by_category.map((c, i) => {
                const cat = getCat(c.category);
                const pct = summary.total > 0 ? (c.total / summary.total * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color} w-32 text-center truncate`}>{cat.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-medium w-28 text-right">{fmt(c.total)}</span>
                    <span className="text-xs text-slate-400 w-12 text-right">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly grouped list */}
      {monthKeys.length > 0 ? (
        <div className="space-y-4">
          {monthKeys.map(month => {
            const entries = grouped[month];
            const monthTotal = entries.reduce((s, r) => s + r.amount, 0);
            return (
              <Card key={month} className="border-slate-200" data-testid={`month-${month}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {getMonthLabel(month)}
                    </CardTitle>
                    <span className="text-lg font-bold text-emerald-700">{fmt(monthTotal)}</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="divide-y divide-slate-100">
                    {entries.map(r => {
                      const cat = getCat(r.category);
                      return (
                        <div key={r.id} className="flex items-center gap-3 py-2.5 group">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.color} flex-shrink-0`}>{cat.label}</span>
                          {r.payment_method && <span className="text-xs text-slate-400 flex-shrink-0">{getPM(r.payment_method)}</span>}
                          <span className="text-sm text-slate-700 flex-1 truncate">{r.description || '–'}</span>
                          <span className="text-sm font-semibold text-slate-900">{fmt(r.amount, r.currency)}</span>
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(r)}><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(r.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-1">Aucune recette pour {filterYear}</h3>
            <p className="text-slate-500 mb-4">Ajoutez vos revenus mois par mois</p>
            <Button onClick={() => { setForm({ ...defaultForm, month: `${filterYear}-01` }); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />Premiere recette
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle>{editing ? 'Modifier la recette' : 'Nouvelle recette'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mois</Label>
                <Select value={form.month.split('-')[1]} onValueChange={(v) => setForm({ ...form, month: `${form.month.split('-')[0]}-${v}` })}>
                  <SelectTrigger data-testid="revenue-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Annee</Label>
                <Select value={form.month.split('-')[0]} onValueChange={(v) => setForm({ ...form, month: `${v}-${form.month.split('-')[1]}` })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Type de recette</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="revenue-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVENUE_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Moyen de paiement</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PAYMENT_METHODS.map(m => (
                  <button key={m.id} type="button" onClick={() => setForm({ ...form, payment_method: m.id })}
                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${form.payment_method === m.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                    data-testid={`pm-${m.id}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Montant *</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="1200.00" required data-testid="revenue-amount" />
              </div>
              <div>
                <Label>Devise</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHF">CHF</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Ventes Shopify aout, Prestations conseil..." data-testid="revenue-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-revenue-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? 'Modifier' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevenuesPage;
